import React, {useEffect} from 'react';
import Utils from '../services/Utils';
import {useHistory} from 'react-router-dom';
import {FormattedMessage} from 'react-intl';
import {faArrowPointer, faCalculator, faCartShopping, faEye} from '@fortawesome/free-solid-svg-icons';
import {
  ConversionFunnel,
  KpiTile,
  PaymentsProgressTrackWidget,
  RankingWidget,
  SalesValueWidget,
  VehiclesProgressTrackWidget,
  YearSelector,
} from '../components/Widgets';
import API from '../services/API';

export default (props) => {
  Utils.setBackground(false);

  const {intl} = props;
  const history = useHistory();
  const [stats, setStats] = React.useState({});
  const [years, setYears] = React.useState([]);
  const [activeYear, setActiveYear] = React.useState(null);
  const [lengthUnit, setLengthUnit] = React.useState('KM');
  const [formatter, setFormatter] = React.useState(new Intl.NumberFormat(navigator.language, {style: 'currency', currency: 'EUR'}));
  const numberFormatter = new Intl.NumberFormat(navigator.language);

  useEffect(() => {
    if (!Utils.isAccessToken(history)) {
      return;
    }

    API.getSalesStats(history,).then(response => {
      if (response && response.data.success === true) {
        const data = response.data.data;
        let yearsKeys = Object.keys(data.sales || {}).map(y => Number(y)).sort((a, b) => a - b);
        // Move the "0" (TOTAL) to the end if present
        if (yearsKeys.length > 0 && yearsKeys[0] === 0) {
          yearsKeys = yearsKeys.slice(1).concat(0);
        }
        setYears(yearsKeys);
        // Set active year to the latest real year (exclude 0 if present)
        const nonZeroYears = yearsKeys.filter(y => y !== 0);
        // ?? (et non ||) pour ne pas reconvertir en null l'année 0 (TOTAL) si c'est la seule.
        const initialYear = nonZeroYears[nonZeroYears.length - 1] ?? yearsKeys[yearsKeys.length - 1];
        setActiveYear(initialYear ?? null);
        setStats(data);
        setLengthUnit(response.data.data.lengthUnit === 'MILE' ? 'MI' : 'KM');
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });

    API.getCurrency(history).then(response => {
      if (response && response.data.success === true) {
        setFormatter(new Intl.NumberFormat(navigator.language, {
          style: 'currency',
          currency: response.data.data.code,
        }));
      }
    });
  }, []);

  const onPrevItem = () => {
    const idx = years.indexOf(activeYear);
    if (idx > 0) {
      setActiveYear(years[idx - 1]);
    }
  };

  const onNextItem = () => {
    const idx = years.indexOf(activeYear);
    if (idx !== -1 && idx < years.length - 1) {
      setActiveYear(years[idx + 1]);
    }
  };

  // ── Dérivés d'affichage ──
  const isCurrentYear = Number(activeYear) === new Date().getFullYear();
  const isYearly = activeYear === 0; // 0 = pseudo-année « TOTAL »
  const m = (k) => stats?.[k]?.[activeYear];
  // Taux de conversion garde la division par zéro / données partielles → null ('—').
  const rate = (a, b) => (Number.isFinite(a) && Number.isFinite(b) && b > 0 ? Math.max(0, Math.min(100, Math.round(a / b * 100))) : null);

  // Parcours client = entonnoir Visiteurs → Estimations → Accès → Réservations.
  const journey = [
    {key: 'visitors', step: 1, icon: faEye, label: 'UNIQUE_VISITORS'},
    {key: 'estimations', step: 2, icon: faCalculator, label: 'PRICE_ESTIMATES'},
    {key: 'bookingAccess', step: 3, icon: faArrowPointer, label: 'ACCESS_TO_BOOKING_PAGE'},
    {key: 'bookings', step: 4, icon: faCartShopping, label: 'BOOKINGS'},
  ];
  const totals = journey.map(j => m(j.key)?.total);
  const overall = rate(totals[3], totals[0]);

  return (
    <div className="ccv2-dashboard">
      <header className="ccv2-dash-head">
        <div className="ccv2-page-head">
          <h1 className="ccv2-page-title"><FormattedMessage id="DASHBOARD"/></h1>
          <p className="ccv2-page-subtitle"><FormattedMessage id="DASHBOARD_SUBTITLE"/></p>
        </div>
        <YearSelector years={years} activeYear={activeYear} onPrev={onPrevItem} onNext={onNextItem}
                      onPick={setActiveYear} intl={intl}/>
      </header>

      {/* HÉRO — chiffre d'affaires (graphique ApexCharts conservé) */}
      <SalesValueWidget
        title={intl.formatMessage({id: 'TURNOVER'})}
        formatter={formatter}
        percentage={stats?.evolution}
        data={stats?.sales?.[activeYear]}
        isCurrentYear={isCurrentYear}
      />

      <div className="ccv2-dash-section"><FormattedMessage id="CUSTOMER_JOURNEY"/></div>

      <section className="ccv2-kpi-grid">
        {journey.map(j => (
          <KpiTile key={j.key} step={j.step} icon={j.icon}
                   label={intl.formatMessage({id: j.label})} metric={m(j.key)}
                   isYearly={isYearly} numberFormatter={numberFormatter}/>
        ))}
      </section>

      <ConversionFunnel steps={journey} totals={totals} rate={rate} overall={overall}
                        numberFormatter={numberFormatter} intl={intl}/>

      <section className="ccv2-cockpit">
        <div className="ccv2-cockpit-main">
          <RankingWidget data={stats?.vehicles?.[activeYear]} formatter={formatter} lengthUnit={lengthUnit}/>
        </div>
        <div className="ccv2-cockpit-side">
          <VehiclesProgressTrackWidget intl={intl} data={stats?.vehicleBookingDistribution?.[activeYear]}/>
          <PaymentsProgressTrackWidget intl={intl} data={stats?.paymentModesDistribution?.[activeYear]}/>
        </div>
      </section>
    </div>
  );
};
